///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useDispatch } from "react-redux";
import { isEqual } from "lodash";

import PanelStateIndicator from "../PanelStateIndicator";
import Avatar from "../../Avatar";
import EditIcon from "../../icons/EditIcon";
import TeamEditModal from "./TeamEditModal";
import {
  openTeamEditModal,
  resetTeamEditState
} from "../../../slices/bidSummarySlice";
import messages from "../../../helpers/messages";

function Team({ loading, error, fetchDetails, team, bidId }) {
  const dispatch = useDispatch();

  const BidTeamMember = props => (
    <div
      className="inline-block align-top pl-24"
      data-testid={props["data-testid"]}
      style={{ width: "247.75px" }}
    >
      <Avatar
        className="inline-block"
        imageSource={props.data.profilePicture}
        size={53}
        data-testid={`${props["data-testid"]}-avatar`}
        alt={props.data.fullName}
        textContent={`${props.data.firstName[0]}${props.data.lastName[0]}`}
        fontSize={16}
      />
      <div className="grid__cell t75 pl-16">
        <p className="font-size-14 mb-4">{props.data.fullName}</p>
        <p className="font-bold zeta mb-4">{props.data.jobTitle}</p>
        {props.data.secondaryJobTitle && (
          <p className="font-bold zeta mb-4">{props.data.secondaryJobTitle}</p>
        )}
        <p className="zeta mb-4">{props.data.workPhone}</p>
        {props.isBidOwner && (
          <p className="bid-owner font-size-10">
            {messages.bidOwner.defaultMessage}
          </p>
        )}
      </div>
    </div>
  );

  const handleEditIconClick = () => {
    dispatch(openTeamEditModal());
  };

  const closeTeamEditModal = () => {
    dispatch(resetTeamEditState());
  };

  return (
    <div className="team card pos-relative">
      <div className="pos-relative uppercase widget" data-testid="team-widget">
        <span className="inline-block pt-8 pb-8">
          {messages.team.defaultMessage}
        </span>
        {team && (
          <EditIcon
            height="18px"
            width="18px"
            className="cursor-pointer fr m-16 mr-24"
            onClick={handleEditIconClick}
          />
        )}
      </div>

      {(loading || !team || error) && (
        <div
          style={{ height: 117 }}
          className={
            "team-inidicator border-top-grey box " + (error ? "p-24" : "")
          }
        >
          <PanelStateIndicator
            iconSize="32px"
            loading={loading}
            error={error}
            handleRefresh={fetchDetails}
          />
        </div>
      )}

      {team && (
        <div style={{ height: 120 }} className="border-top-grey">
          <div
            className="pt-20 scroll overflow--auto nowrap"
            data-testid="team"
            style={{ height: 117 }}
          >
            {!isEqual(team.salesRepresentative, team.truckloadPricing) && (
              <BidTeamMember
                data-testid="main-sales-rep"
                data={team.salesRepresentative}
              />
            )}
            {team.truckloadPricing && (
              <BidTeamMember
                data-testid="truckload-pricing"
                data={team.truckloadPricing}
                isBidOwner={true}
              />
            )}
            {team.lessThanTruckloadPricing && (
              <BidTeamMember
                data-testid="less-than-truckload-pricing"
                data={team.lessThanTruckloadPricing}
                isBidOwner={true}
              />
            )}
            {team.intermodalPricing && (
              <BidTeamMember
                data-testid="intermodal-pricing"
                data={team.intermodalPricing}
                isBidOwner={true}
              />
            )}
            {team.directorOfCustomerSuccessAndOpportunityLeader && (
              <BidTeamMember
                data-testid="director-of-customer-success-and-opportunity-leader"
                data={team.directorOfCustomerSuccessAndOpportunityLeader}
              />
            )}
            {!team.directorOfCustomerSuccessAndOpportunityLeader && team.directorOfCustomerSuccess && (
              <BidTeamMember
                data-testid="director-of-customer-success"
                data={team.directorOfCustomerSuccess}
              />
            )}
            {!team.directorOfCustomerSuccessAndOpportunityLeader && team.opportunityLeader && (
              <BidTeamMember
                data-testid="opportunity-leader"
                data={team.opportunityLeader}
              />
            )}
            {team.subscribers &&
              team.subscribers.map((s, index) => {
                return (
                  <BidTeamMember
                    key={"subscriber_" + index}
                    data-testid={"subscriber_" + index}
                    data={s}
                  />
                );
              })}
          </div>
        </div>
      )}

      {team && (
        <TeamEditModal
          closeModal={closeTeamEditModal}
          bidId={bidId}
          team={team}
        />
      )}
    </div>
  );
}

export default Team;
